import { FEAT, SKILL_FX, SKILLS, formFx } from '@somnhold/content';
import { hexToWorld } from '../hex.ts';
import { cosA, dist2, idiv, imin, mulBp, sinA, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, Structure, UqDue } from '../types.ts';
import { damageEnemy, enemiesNear, ev, healHero, targetable, wardHero } from './combat.ts';
import { featPieceAllies, featPieceTest, featPieceWatch, featUse } from './feats.ts';
import { formFinal } from './forms.ts';
import { corrupt } from './kits.ts';
import { bigOne, due, heroById, pieceStun } from './panoply.ts';
import { enemiesInCone } from './skills.ts';
import { fx, hasUq, nearSegment } from './uniques.ts';

/**
 * The paladin's panoply in a fight (docs/PANOPLIA.md §10): its nine pieces, called by the skills' programs (cast.ts
 * `special` steps), by the blows on a hero, a nightmare or a building (combat.ts), by a basic blow (heroHitExtras), by a
 * nightmare's death and by the queue of what comes later (panoply.ts). Its burns are holy, never fire.
 */

const live = (s: BattleState, o: Enemy): boolean => o.hp > 0 && targetable(s, o) && o.kind !== 'boneWall';

const heroOfPid = (s: BattleState, pid: string): Hero | undefined => s.heroes.find((h) => h.pid === pid);

/* ---------- Martillo de la primera ronda (Luz cegadora) ---------- */

/**
 * The first round's hammer, after the flash: it goes out behind the paladin too (`front`: what the flash caught ahead,
 * `bt` how long it blinds, `bd` what it burns). Ceguera (its final form): what it blinded can't strike from afar.
 */
export const backFlash = (s: BattleState, h: Hero, aim: { x: number; y: number }, r: number, deg: number, bt: number, bd: number, front: Enemy[]): void => {
  const back = enemiesInCone(s, h, 2 * h.x - aim.x, 2 * h.y - aim.y, r, deg).filter((o) => !front.includes(o));
  const bp = mulBp(SKILL_FX.blindingLight.blindBp, fx('firstRoundHammer', h).blindBp as number);
  for (const o of back) {
    if ((o.blind ?? 0) < bt) o.blind = bt;
    o.blindBp = bp;
    damageEnemy(s, o, bd, { pid: h.pid, type: 'holy' });
  }
  ev(s, { t: 'boom', x: h.x, y: h.y, r, kind: 'blindBack' });
  featUse(s, h, 'firstRoundHammer', back.length);
  if (front.length + back.length >= FEAT.need.blind) featPieceTest(s, h, 'firstRoundHammer');
  if (formFinal(h, 'firstRoundHammer')) for (const o of [...front, ...back]) if (o.hp > 0) o.hushUntil = s.tick + bt;
};

/**
 * Whether a blinded nightmare can't strike from afar now (Ceguera, §8): an elite, half the time (a second on, a
 * second off); a boss always can, at half (`hushBlow`).
 */
export const hushed = (s: BattleState, e: Enemy): boolean =>
  (e.hushUntil ?? 0) > s.tick && (e.blind ?? 0) > 0 && !bigOne(e) && (!e.elite || idiv(s.tick, 12) % 2 === 0);

/** A blow from afar under Ceguera: a boss's lands at half. */
export const hushBlow = (s: BattleState, e: Enemy, dmg: number): number => ((e.hushUntil ?? 0) > s.tick && (e.blind ?? 0) > 0 && bigOne(e) ? idiv(dmg, 2) : dmg);

/* ---------- Martillo de la penitencia (Expiar) ---------- */

/** The penitence hammer, at the atonement: how many times a kill re-arms it, and for how long. */
export const atoneChainOn = (h: Hero, ticks: number): void => {
  h.atoneLeft = fx('penitenceHammer', h).chain as number;
  h.atoneLen = ticks;
  h.atoneKills = 0;
  h.atoneChained = 0;
};

/** An atoning blow landed (`heal`: what it mended): a kill arms the next; Absolución (its final form) lands around too. */
const atoneLanded = (s: BattleState, h: Hero, e: Enemy, dealt: number, heal: number): void => {
  if (h.atoneChained) featUse(s, h, 'penitenceHammer', mulBp(heal, fx('penitenceHammer', h).healBp as number));
  if (formFinal(h, 'penitenceHammer')) {
    const r = formFx('penitenceHammer', 'final').radius as number;
    for (const o of enemiesNear(s, e.x, e.y, r, (x) => x.id !== e.id && live(s, x))) damageEnemy(s, o, dealt, { pid: h.pid, type: 'holy' });
    ev(s, { t: 'boom', x: e.x, y: e.y, r, kind: 'absolution' });
  }
  const killed = e.hp <= 0;
  h.atoneKills = killed ? (h.atoneKills ?? 0) + 1 : 0;
  if (h.atoneKills >= FEAT.need.atones) {
    featPieceTest(s, h, 'penitenceHammer');
    h.atoneKills = 0;
  }
  h.atoneChained = 0;
  if (killed && (h.atoneLeft ?? 0) > 0) {
    h.atoneLeft = (h.atoneLeft ?? 0) - 1;
    h.atone = h.atoneLen ?? 0;
    h.atoneChained = 1;
    ev(s, { t: 'boom', x: h.x, y: h.y, r: 700, kind: 'atone' });
  }
};

/* ---------- Martillo del guardallaves (Sello) ---------- */

/** The keeper's hammer, at the seal: whose it is and who strikes it; Dos sellos (its final form), the nearest other too. */
export const keeperSeal = (s: BattleState, h: Hero, e: Enemy): void => {
  e.sealBy = h.id;
  e.sealHit = [];
  if (!formFinal(h, 'keeperHammer')) return;
  let o: Enemy | null = null;
  for (const x of enemiesNear(s, e.x, e.y, SKILLS.holySeal.castRange, (x) => x.id !== e.id && live(s, x))) {
    if (!o || dist2(x.x, x.y, e.x, e.y) < dist2(o.x, o.y, e.x, e.y) || (dist2(x.x, x.y, e.x, e.y) === dist2(o.x, o.y, e.x, e.y) && x.id < o.id)) o = x;
  }
  if (!o) return;
  o.seal = e.seal;
  o.sealBp = e.sealBp;
  o.sealBurn = e.sealBurn ? { ...e.sealBurn } : undefined;
  o.sealBy = h.id;
  o.sealHit = [];
  ev(s, { t: 'beam', x: e.x, y: e.y, tx: o.x, ty: o.y, kind: 'twoSeals' });
  ev(s, { t: 'boom', x: o.x, y: o.y, r: 600, kind: 'holySeal' });
};

/** A hero struck a sealed nightmare (combat.ts): it is among those the seal mends when it falls. */
export const sealStruck = (e: Enemy, h: Hero): void => {
  const hit = (e.sealHit ??= []);
  if (!hit.includes(h.id)) hit.push(h.id);
};

const sealFell = (s: BattleState, h: Hero, e: Enemy): void => {
  const bp = fx('keeperHammer', h).healBp as number;
  const healed: Hero[] = [];
  let n = 0;
  for (const id of e.sealHit ?? []) {
    const o = heroById(s, id);
    if (!o || o.downTicks > 0) continue;
    n += healHero(s, o, mulBp(o.stats.maxHp, bp), h.pid);
    healed.push(o);
  }
  if (!healed.length) return;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 800, kind: 'sealBlood' });
  featUse(s, h, 'keeperHammer', n);
  if (healed.some((o) => o.id !== h.id)) featPieceAllies(s, h, 'keeperHammer', healed);
};

/* ---------- Coraza del maestro de armas (Martillo giratorio) ---------- */

/** The arms master's cuirass, at the spinning hammer: its flight at the end goes in the queue (`dmg`: the hammer's blow). */
export const hammerLoose = (s: BattleState, h: Hero, ticks: number, dmg: number): void => {
  due(s, { at: s.tick + ticks, k: 'hammerLoose', hid: h.id, x: h.x, y: h.y, n: [dmg] });
};

const hammerLooseRun = (s: BattleState, h: Hero, d: UqDue): void => {
  const f = fx('armsMasterCuirass', h);
  const dmg = mulBp(d.n?.[0] ?? 0, f.damageBp as number);
  const len = f.length as number;
  // Ahead of the paladin; with Dos martillos (its final form), behind it too.
  const dirs = formFinal(h, 'armsMasterCuirass') ? [h.facing, h.facing + 512] : [h.facing];
  let hits = 0;
  let dealt = 0;
  for (const a of dirs) {
    const tx = h.x + idiv(cosA(a) * len, 10000);
    const ty = h.y + idiv(sinA(a) * len, 10000);
    for (const o of s.enemies) {
      if (!live(s, o) || !nearSegment(o.x, o.y, h.x, h.y, tx, ty, f.width as number)) continue;
      dealt += damageEnemy(s, o, dmg, { pid: h.pid, type: 'holy' });
      hits++;
    }
    ev(s, { t: 'beam', x: h.x, y: h.y, tx, ty, kind: 'hammerLoose' });
  }
  featUse(s, h, 'armsMasterCuirass', dealt);
  if (hits >= FEAT.need.hammer) featPieceTest(s, h, 'armsMasterCuirass');
};

/* ---------- Cota del armero (Bendecir el arma) ---------- */

/** The armourer's coat, at the blessing: whose it is, and a share of it on the ally nearest the blessed one. */
export const blessShare = (s: BattleState, h: Hero, a: Hero): void => {
  a.blessBy = h.id;
  h.blessKills = 0;
  const share = 10000 + mulBp((a.blessBp ?? 10000) - 10000, fx('armourerCoat', h).shareBp as number);
  let o: Hero | null = null;
  for (const x of s.heroes) {
    if (x.id === a.id || x.downTicks > 0 || !withinRange(x.x, x.y, a.x, a.y, SKILLS.blessWeapon.castRange)) continue;
    if (!o || dist2(x.x, x.y, a.x, a.y) < dist2(o.x, o.y, a.x, a.y)) o = x;
  }
  if (!o || ((o.blessed ?? 0) > 0 && (o.blessBp ?? 10000) >= share)) return;
  o.blessed = a.blessed;
  o.blessBp = share;
  o.blessBy = h.id;
  ev(s, { t: 'beam', x: a.x, y: a.y, tx: o.x, ty: o.y, kind: 'blessShare' });
  ev(s, { t: 'boom', x: o.x, y: o.y, r: 500, kind: 'blessWeapon' });
};

/** A blessed blow (heroHitExtras): it counts for the coat of the paladin who blessed it; Arma santa (its final form) mends. */
const blessedHit = (s: BattleState, h: Hero, dealt: number): void => {
  const p = heroById(s, h.blessBy ?? -1);
  if (!p || !hasUq(p, 'armourerCoat')) return;
  featUse(s, p, 'armourerCoat', dealt);
  if (formFinal(p, 'armourerCoat')) healHero(s, h, mulBp(h.stats.maxHp, formFx('armourerCoat', 'final').healBp as number), p.pid);
};

/* ---------- Placas de la escalera (Intervenir) ---------- */

/**
 * The stair plates, on landing by the ally: what is around it is stunned. Siempre ahí (its final form): an ally that
 * low leaves the intervention ready again (once every `every` ticks). Returns whether it did.
 */
export const stairStun = (s: BattleState, h: Hero, a: Hero, ticks: number): boolean => {
  const f = fx('stairPlates', h);
  let n = 0;
  for (const o of enemiesNear(s, a.x, a.y, f.radius as number, (x) => live(s, x))) {
    const st = pieceStun(o, f.stun as number);
    if (st > o.stun) o.stun = st;
    if (st > 0) n++;
  }
  ev(s, { t: 'boom', x: a.x, y: a.y, r: f.radius as number, kind: 'stairStun' });
  featUse(s, h, 'stairPlates', n);
  if (a.hp * 10000 < a.stats.maxHp * FEAT.need.interveneLowBp && n >= FEAT.need.interveneStun) featPieceWatch(s, h, 'stairPlates', [a.id], ticks, -1);
  if (!formFinal(h, 'stairPlates')) return false;
  const ff = formFx('stairPlates', 'final');
  if (a.hp * 10000 >= a.stats.maxHp * (ff.belowBp as number) || (h.stairAt !== undefined && s.tick - h.stairAt < (ff.every as number))) return false;
  h.stairAt = s.tick;
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 600, kind: 'alwaysThere' });
  return true;
};

/* ---------- Piedra bendita (Muralla sagrada) ---------- */

/** The blessed stone, at the rampart: with no building to cover, it covers the paladin as one (`covered`: how many). */
export const stoneSelf = (h: Hero, covered: number, ticks: number): void => {
  h.wallBurnt = [];
  if (covered > 0) return;
  h.stoneT = ticks;
  wardHero(h, SKILL_FX.holyRampart.takenBp, ticks);
};

/** What struck something the stone covers burns (holy) for a share of the blow; the ids burnt in this rampart, for its test. */
const stoneBurn = (s: BattleState, h: Hero, from: Enemy, amount: number): void => {
  if (from.hp <= 0) return;
  const got = damageEnemy(s, from, mulBp(amount, fx('blessedStone', h).burnBp as number), { pid: h.pid, type: 'holy' });
  if (got <= 0) return;
  featUse(s, h, 'blessedStone', got);
  const burnt = (h.wallBurnt ??= []);
  if (burnt.includes(from.id)) return;
  burnt.push(from.id);
  if (burnt.length === FEAT.need.rampart) featPieceTest(s, h, 'blessedStone');
};

/**
 * A building under a blessed stone's rampart is struck (combat.ts, before it loses the life): the striker burns, and with
 * Piedra que no cede (its final form) the building keeps 1. Returns what the building takes.
 */
export const stoneStruck = (s: BattleState, st: Structure, amount: number, from: Enemy | null | undefined): number => {
  for (const z of s.zones) {
    if (z.kind !== 'rampart' || !z.coverBp) continue;
    const c = hexToWorld(st.q, st.r);
    if (!withinRange(z.x, z.y, c.x, c.y, z.radius)) continue;
    const h = heroOfPid(s, z.pid);
    if (!h || !hasUq(h, 'blessedStone')) continue;
    if (from) stoneBurn(s, h, from, amount);
    return formFinal(h, 'blessedStone') ? imin(amount, st.hp - 1) : amount;
  }
  return amount;
};

/* ---------- Dije del hermano mayor (Sacrificio) ---------- */

/** The elder's charm, at the sacrifice: the allies it covers now, watched until it ends (nobody falling is its test). */
export const sacrificeWatch = (s: BattleState, h: Hero, ticks: number): void => {
  const ids = s.heroes.filter((o) => o.id !== h.id && o.downTicks === 0 && withinRange(o.x, o.y, h.x, h.y, h.sacrificeR ?? 0)).map((o) => o.id);
  if (ids.length) due(s, { at: s.tick + ticks, k: 'sacrificeEnd', hid: h.id, x: h.x, y: h.y, ids });
};

const sacrificeEnd = (s: BattleState, h: Hero, d: UqDue): void => {
  const all = (d.ids ?? []).map((id) => heroById(s, id));
  if (all.some((o) => !o || o.downTicks > 0)) return;
  featPieceAllies(s, h, 'elderCharm', all as Hero[]);
};

/** A blow carried by the sacrifice (combat.ts): what struck burns (holy) for a share of it. */
export const sacrificeBurn = (s: BattleState, p: Hero, from: Enemy, moved: number): void => {
  if (!hasUq(p, 'elderCharm') || from.hp <= 0) return;
  damageEnemy(s, from, mulBp(moved, fx('elderCharm', p).burnBp as number), { pid: p.pid, type: 'holy' });
  featUse(s, p, 'elderCharm', moved);
};

/* ---------- Cuenta de la oración (Avatar de luz) ---------- */

/** The prayer bead, at the avatar: its hammers' kills begin, and how much Luz que no se apaga can still lengthen it. */
export const avatarStart = (h: Hero, ticks: number): void => {
  h.hammerKills = 0;
  h.avatarMax = ticks;
};

/** Every few ticks of the avatar, a hammer of light on the nearest inked nightmare in reach (else the nearest). */
const lightHammer = (s: BattleState, h: Hero): void => {
  const f = fx('prayerBead', h);
  if ((h.areaBlow ?? 0) % (f.every as number) !== 0) return;
  let best: Enemy | null = null;
  let inked = false;
  let bd = 0;
  for (const o of enemiesNear(s, h.x, h.y, f.range as number, (x) => live(s, x))) {
    const c = corrupt(o);
    const dd = dist2(o.x, o.y, h.x, h.y);
    if (!best || (c && !inked) || (c === inked && (dd < bd || (dd === bd && o.id < best.id)))) {
      best = o;
      inked = c;
      bd = dd;
    }
  }
  if (!best) return;
  const got = damageEnemy(s, best, mulBp(mulBp(h.stats.attack, h.stats.powerBp), f.damageBp as number), { pid: h.pid, type: 'holy' });
  ev(s, { t: 'boom', x: best.x, y: best.y, r: 500, kind: 'lightHammer' });
  featUse(s, h, 'prayerBead', got);
  if (best.hp <= 0) {
    h.hammerKills = (h.hammerKills ?? 0) + 1;
    if (h.hammerKills === FEAT.need.hammers) featPieceTest(s, h, 'prayerBead');
  }
};

/* ---------- the hooks ---------- */

/** A paladin's (or a blessed ally's) basic blow landed (heroHitExtras); `heal` what an atonement in it mended (−1: none). */
export const paladinHit = (s: BattleState, h: Hero, e: Enemy, dealt: number, heal: number): void => {
  if (heal >= 0 && hasUq(h, 'penitenceHammer')) atoneLanded(s, h, e, dealt, heal);
  if (h.blessBy !== undefined && (h.blessed ?? 0) > 0) blessedHit(s, h, dealt);
};

/** A blow on a hero (combat.ts, after it lost the life): the stone that covers the paladin burns what struck. */
export const paladinHurt = (s: BattleState, h: Hero, dmg: number, from: Enemy | null | undefined): void => {
  if (from && (h.stoneT ?? 0) > 0 && hasUq(h, 'blessedStone')) stoneBurn(s, h, from, dmg);
};

/** About to fall (combat.ts): El que se queda (the charm's final form) and the stone that doesn't give keep the paladin at 1. */
export const paladinHoldUp = (h: Hero): void => {
  if ((h.sacrifice ?? 0) > 0 && formFinal(h, 'elderCharm')) {
    h.hp = 1;
    h.sacrifice = 0;
  } else if ((h.stoneT ?? 0) > 0 && formFinal(h, 'blessedStone')) h.hp = 1;
};

/** A nightmare died: the seal's mending, the blessed ones' kills, the avatar that doesn't go out. */
export const paladinDied = (s: BattleState, e: Enemy): void => {
  if (e.sealBy !== undefined && (e.seal ?? 0) > 0) {
    const h = heroById(s, e.sealBy);
    if (h && hasUq(h, 'keeperHammer')) sealFell(s, h, e);
  }
  const k = e.lastPid ? heroOfPid(s, e.lastPid) : undefined;
  if (!k) return;
  if (k.blessBy !== undefined && (k.blessed ?? 0) > 0) {
    const p = heroById(s, k.blessBy);
    if (p && hasUq(p, 'armourerCoat')) {
      p.blessKills = (p.blessKills ?? 0) + 1;
      if (p.blessKills === FEAT.need.blessKills) featPieceTest(s, p, 'armourerCoat');
    }
  }
  if ((k.areaBlow ?? 0) > 0 && k.areaHoly && (k.avatarMax ?? 0) > 0 && corrupt(e) && formFinal(k, 'prayerBead')) {
    const add = imin(formFx('prayerBead', 'final').extra as number, k.avatarMax ?? 0);
    k.areaBlow = (k.areaBlow ?? 0) + add;
    k.avatarMax = (k.avatarMax ?? 0) - add;
  }
};

/** Every tick, for a standing paladin with a piece: the stone's cover, the avatar's hammers. */
export const paladinTick = (s: BattleState, h: Hero): void => {
  if (h.stoneT && h.stoneT > 0) h.stoneT--;
  if ((h.areaBlow ?? 0) > 0 && h.areaHoly && hasUq(h, 'prayerBead')) lightHammer(s, h);
};

/** What the paladin's pieces left for this tick (panoply.ts runs the queue). Returns whether it was one of them. */
export const paladinDue = (s: BattleState, h: Hero, d: UqDue): boolean => {
  if (d.k === 'hammerLoose') hammerLooseRun(s, h, d);
  else if (d.k === 'sacrificeEnd') sacrificeEnd(s, h, d);
  else return false;
  return true;
};
